import { DataActSection, DataActQuestion, DataActAnswer } from "../types";

type AnswerMap = Record<string, string>;

function buildAnswerMap(answers: DataActAnswer[]): AnswerMap {
  const map: AnswerMap = {};
  for (const a of answers) {
    map[a.questionId] = a.answer;
  }
  return map;
}

function matchesSimpleCondition(
  condition: Record<string, unknown>,
  answerMap: AnswerMap,
): boolean {
  for (const [key, expected] of Object.entries(condition)) {
    if (key === "$or") continue;
    const actual = answerMap[key];
    if (!actual) return false;
    if (Array.isArray(expected)) {
      if (!expected.includes(actual)) return false;
    } else {
      if (actual !== expected) return false;
    }
  }
  return true;
}

function evaluateCondition(
  condition: Record<string, unknown> | null,
  answerMap: AnswerMap,
): boolean {
  if (!condition) return true;

  if ("$or" in condition) {
    const orClauses = condition["$or"] as Record<string, unknown>[];
    if (
      !orClauses.some((clause) => matchesSimpleCondition(clause, answerMap))
    ) {
      return false;
    }
    const rest = Object.fromEntries(
      Object.entries(condition).filter(([k]) => k !== "$or"),
    );
    if (Object.keys(rest).length > 0) {
      return matchesSimpleCondition(rest, answerMap);
    }
    return true;
  }

  return matchesSimpleCondition(condition, answerMap);
}

export function isSectionVisible(
  section: DataActSection,
  answers: DataActAnswer[],
): boolean {
  const answerMap = buildAnswerMap(answers);
  return evaluateCondition(section.visibleWhen, answerMap);
}

export function isQuestionVisible(
  question: DataActQuestion,
  answers: DataActAnswer[],
): boolean {
  const answerMap = buildAnswerMap(answers);
  return evaluateCondition(
    (question.visibleWhen as Record<string, unknown>) ?? null,
    answerMap,
  );
}

export function getVisibleSections(
  sections: DataActSection[],
  answers: DataActAnswer[],
): DataActSection[] {
  const answerMap = buildAnswerMap(answers);
  return sections.filter(
    (s) =>
      evaluateCondition(s.visibleWhen, answerMap) &&
      s.questions.some(
        (q) =>
          !q.hidden &&
          evaluateCondition(
            (q.visibleWhen as Record<string, unknown>) ?? null,
            answerMap,
          ),
      ),
  );
}

/**
 * Returns the section title prefixed with its fixed per-path number (e.g.
 * "4. Data Processing Service Assessment" on the service path). Section
 * numbers come from `sectionLabel` in the questionnaire JSON — mirroring
 * `questionLabel` on questions — and never shift when intermediate sections
 * are hidden by branching.
 */
export function sectionDisplayTitle(
  section: DataActSection,
  pathKey: string | null,
): string {
  const labels = section.sectionLabel as
    | Record<string, string | undefined>
    | undefined;
  const number = pathKey ? labels?.[pathKey] : undefined;
  return number ? `${number}. ${section.title}` : section.title;
}

export function getVisibleQuestions(
  section: DataActSection,
  answers: DataActAnswer[],
): DataActQuestion[] {
  const answerMap = buildAnswerMap(answers);
  return section.questions.filter(
    (q) =>
      !q.hidden &&
      evaluateCondition(
        (q.visibleWhen as Record<string, unknown>) ?? null,
        answerMap,
      ),
  );
}

function findQuestionOwner(
  sections: DataActSection[],
  questionId: string,
): { section: DataActSection; question: DataActQuestion } | null {
  for (const section of sections) {
    const question = section.questions.find((q) => q.id === questionId);
    if (question) return { section, question };
  }
  return null;
}

function reachableAnswers(
  sections: DataActSection[],
  answers: DataActAnswer[],
): DataActAnswer[] {
  return answers.filter((a) => {
    const owner = findQuestionOwner(sections, a.questionId);
    // Synthetic keys (e.g. the linked-product flow's `__internal:` state) are
    // not part of the question graph, so leave them untouched.
    if (!owner) return true;
    return (
      isSectionVisible(owner.section, answers) &&
      isQuestionVisible(owner.question, answers)
    );
  });
}

/**
 * Removes answers whose question or section is no longer reachable given the
 * current answers, iterating to a fixed point. Branching questions form a
 * chain (Q2.2 depends on Q2.1, Q2.3 on Q2.2, …), so dropping one answer can
 * hide the next; repeating until the set stops shrinking collapses the whole
 * downstream chain when an earlier branch changes.
 *
 * Visibility is evaluated on `visibleWhen` only (via isSectionVisible /
 * isQuestionVisible), never the `hidden` flag, so prefilled identity questions
 * such as Q1.1–Q1.5 are always kept.
 */
export function pruneUnreachableAnswers(
  sections: DataActSection[],
  answers: DataActAnswer[],
): DataActAnswer[] {
  let current = answers;
  let next = reachableAnswers(sections, current);
  while (next.length !== current.length) {
    current = next;
    next = reachableAnswers(sections, current);
  }
  return next;
}
